/* eslint-disable class-methods-use-this */
import React from 'react'
import PropTypes from 'prop-types'
import actions from 'redux-auto'
import {statusTypes} from 'ag-common/tools/fileStatus'
import {SELECTION_LIMIT} from 'ag-common/tools/questionReferenceConstants'
import i18n from 'i18next'
import Api from '#services/api'
import {withAnswer} from '#components/HOC/withAnswerHOC'
import Text from '#components/a/Text'
import Headline from '#components/a/Headline'
import SelectNachweis from '#components/m/SelectNachweis'
import NachweisListeReference from '#components/o/NachweisListe/NachweisListeReference'
import QuestionDescription from './QuestionDescription'

export class QuestionReference extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      inputValue: '',
      nachweise: undefined,
      addButtonDisabled: true,
    }
    this.addNachweisToAnswer = this.addNachweisToAnswer.bind(this)
  }

  async componentDidMount() {
    if (this.isValidAnswer()) {
      const {currentAnswer} = this.props
      this.checkSelectionLimit(currentAnswer.selectedNachweise)
    }
    try {
      const nachweise = await this.getNachweise()
      this.setState({nachweise, addButtonDisabled: false})
    } catch (e) {
      actions.error.writeError({error: e})
    }
  }

  getNachweise = async () => {
    const nachweise = await Api.readNachweisList()
    return (nachweise && nachweise[0] && nachweise[0].ausbildungsnachweise) || []
  }

  getNachweisByInput() {
    return this.state.nachweise.find(n => n.nummer === this.state.inputValue)
  }

  getNachweisNotExistsWarning() {
    return i18n.t('question_reference_nachweis_not_exists', {nummer: this.state.inputValue})
  }

  getNacheisNotConfirmedWarning(nachweis) {
    return i18n.t('question_reference_nachweis_not_confirmed', {nummer: nachweis.nummer})
  }

  getNachweisAlreadySelectedWarning(nachweis) {
    return i18n.t('question_reference_nachweis_already_selected', {nummer: nachweis.nummer})
  }

  onNachweisRemove = id => {
    const {selectedNachweise} = this.props.currentAnswer
    const selectedNachweiseUpdate = selectedNachweise.filter(nachweis => nachweis.id !== id)
    this.props.saveAnswer({selectedNachweise: selectedNachweiseUpdate})
    this.setState({addButtonDisabled: false, warning: undefined})
  }

  onInputChange = event => {
    const inputValue = event.target.value ? Number(event.target.value) : ''
    this.setState({inputValue})
  }

  getSelectedNachweise() {
    return this.isValidAnswer() ? this.props.currentAnswer.selectedNachweise : []
  }

  isValidAnswer = () => {
    const {currentAnswer} = this.props
    return currentAnswer && currentAnswer.selectedNachweise
  }

  nachweisAlreadySelected(nummer) {
    return this.isValidAnswer() && this.props.currentAnswer.selectedNachweise.some(n => n.nummer === nummer)
  }

  updateSelectedNachweise(nachweis) {
    const selectedNachweise = this.getSelectedNachweise()
    const selectedNachweiseUpdate = [...selectedNachweise, nachweis]
    this.props.saveAnswer({selectedNachweise: selectedNachweiseUpdate})
    this.checkSelectionLimit(selectedNachweiseUpdate)
  }

  checkSelectionLimit(selectedNachweise) {
    if (selectedNachweise.length >= SELECTION_LIMIT) {
      this.setState({
        addButtonDisabled: true,
        warning: i18n.t('question_reference_limit_reached', {limit: SELECTION_LIMIT}),
      })
    }
  }

  clearInput() {
    this.setState({inputValue: ''})
  }

  canNotSelect(nachweis) {
    return !nachweis || this.isConfirmed(nachweis.status) || this.nachweisAlreadySelected(nachweis.nummer)
  }

  isConfirmed(status) {
    return status !== statusTypes.confirmed
  }

  addNachweisToAnswer(e) {
    e.preventDefault()
    if (!this.state.inputValue) {
      return
    }
    const nachweis = this.getNachweisByInput()
    this.displayWarnings(nachweis)
    this.clearInput()
    if (this.canNotSelect(nachweis)) {
      return
    }
    this.updateSelectedNachweise(nachweis)
  }

  displayWarnings(nachweis) {
    let warning = ''
    if (!nachweis) {
      warning = this.getNachweisNotExistsWarning()
    } else if (this.isConfirmed(nachweis.status)) {
      warning = this.getNacheisNotConfirmedWarning(nachweis)
    } else if (this.nachweisAlreadySelected(nachweis.nummer)) {
      warning = this.getNachweisAlreadySelectedWarning(nachweis)
    }
    this.setState({warning})
  }

  render() {
    const {question, currentAnswer, isReadOnly} = this.props
    const {inputValue, addButtonDisabled, warning} = this.state
    return (
      <div className="questionReference">
        {this.props.children}
        <QuestionDescription question={question} />
        {!isReadOnly && (
          <>
            <Headline
              text={i18n.t('question_reference_ausbildungsnachweis_header')}
              importance={3}
              modifier="fragment"
            />
            <SelectNachweis
              inputValue={String(inputValue)}
              autoFocus
              onInputChange={this.onInputChange}
              buttonIsDisabled={addButtonDisabled}
              onButtonClick={this.addNachweisToAnswer}
            />
          </>
        )}
        {this.isValidAnswer() && (
          <NachweisListeReference
            nachweise={currentAnswer.selectedNachweise}
            onNachweisRemove={this.onNachweisRemove}
            isReadOnly={isReadOnly}
          />
        )}
        {warning && <Text text={warning} modifier="error" />}
      </div>
    )
  }
}

QuestionReference.propTypes = {
  saveAnswer: PropTypes.func.isRequired,
  currentAnswer: PropTypes.shape({
    selectedNachweise: PropTypes.array,
  }),
  question: PropTypes.object,
  isReadOnly: PropTypes.bool,
  children: PropTypes.node,
}

export default withAnswer(QuestionReference)
